import { useTranslation } from 'react-i18next';
import { useAppDispatch, useAppSelector } from '../../../../../reduxService/hooks';
import { conversionLabel, convertTo, EConversionSIToImperial } from '../../../../conversion';
import './AnchorBar.scss';
import { IAnchorBarProps } from './IAnchorBar';
import { selectProjectInfo, selectMacro1 } from '../../../../store/projectInfo/projectInfoSlice';
import Divider from '@mui/material/Divider';
import MenuItem from '@mui/material/MenuItem';
import Grid from '@mui/material/Grid';
import { setAnchorBar } from '../../../store/project/projectslice';
import { ErrorMessage, Field, useFormik, FormikProvider } from 'formik';
import * as Yup from 'yup';
import Select, { SelectProps } from '@mui/material/Select';
import { RootState } from '../../../../../reduxService';
import React, { useEffect } from 'react';
import { updateValidation } from '../../../../Macro1/store/project/resultsSlice';
import { getMacro1AnchorTypes } from '../../../../api/products/BeMaccaferriProductsHelper';
import { IMacro1AnchorTypeResponse } from '../../../../api/IApiResponse';
import { at } from 'lodash';
import imgAnchorSpacing from '../../../../images/macro2/Macro2_anchor_spacing.png';
import imgRockGrout from '../../../../images/macro2/Macro2_rock_grout.png';
import imgRockGroutImp from '../../../../images/macro2/Macro2_rock_grout_imperiale.png';
import imgAssumedPlasticization from '../../../../images/macro2/Macro2_assumed_plasticization.png';
import Dialog from '../../../../../components/Dialog/Dialog';
import FieldNumber from '../../../../../components/FieldNumber/FieldNumber';

export interface IAnchorBarForm {
    horizontalSpacing: number; // Horizontal spacing between the anchors [m]
    verticalSpacing: number; // Vertical spacing between the anchors [m]
    inclination: number; // Inclination of bar to the horizontal [degrees]
    coefficient: number; // Coefficient (Bustamante, Doix, 1985)
    customAnchorType: boolean; // If custom anchor type
    anchorType: number; // Anchor type
    anchorTypeName: string;
    innerDiameter: number; // Inner diameter of the anchor bar [mm]
    externalDiameter: string; // External diameter of the anchor bar [mm]
    drillingDiameter: number; // Drilling diameter [mm]
    yieldTensileStress: number; // Yield tensile stress of the anchor [MPa]
    customThickness: number; // Custom thickness [mm]
    automaticThickness: boolean; // Automatic thickness
    environmentalAgressivity: string; // Environmental agressivity
    lifespan: number; // Lifespan [years]
    NFP94270: boolean; // NF P 94-270
    bondStress: number; // Bond stress between grout and rock [MPa]
    assumedPlasticization: number; // Assumed plasticization between rock and anchor [m]
}

export const AnchorBarDefault: IAnchorBarForm = {
    horizontalSpacing: 3,
    verticalSpacing: 3,
    inclination: 10,
    coefficient: 1,
    customAnchorType: false,
    anchorType: 0,
    anchorTypeName: 'Steel bars B450C',
    innerDiameter: 0,
    externalDiameter: '20.00',
    drillingDiameter: 50,
    yieldTensileStress: 450,
    customThickness: 0,
    automaticThickness: false,
    environmentalAgressivity: 'Medium',
    lifespan: 50,
    NFP94270: false,
    bondStress: 0.3,
    assumedPlasticization: 0.3,
};

const MuiSelect = (props: SelectProps) => <Select {...props} />;

const AnchorBar = (props: IAnchorBarProps) => {
    const { t } = useTranslation();
    const projectInfo = useAppSelector(selectProjectInfo);
    const dispatch = useAppDispatch();
    const macro1State = useAppSelector(selectMacro1);
    const [anchorTypesList, setAnchorTypesList] = React.useState<IMacro1AnchorTypeResponse[]>([]);
    const [externalDiametersList, setExternalDiametersList] = React.useState<string[]>([]);

    const [CurrentDialog, setCurrentDialog] = React.useState<string>('');
    const openDialog = (dialogCode: string) => {
        setCurrentDialog(dialogCode);
    };
    const closeDialog = () => {
        setCurrentDialog('');
    };

    const checkConversion = (value: number, conversion: EConversionSIToImperial) => {
        return projectInfo.data?.unit == 'Metric' ? value : convertTo(value, conversion);
    };

    const rockGroutSrc = projectInfo.data?.unit == 'Metric' ? imgRockGrout : imgRockGroutImp;

    const AnchorBarValidationSchema = Yup.object().shape({
        horizontalSpacing: Yup.number()
            .min(0.01)
            .label(t('anchor_bar_horizontal_spacing', { unit_measure: '' }))
            .required(),
        verticalSpacing: Yup.number()
            .min(0.01)
            .label(t('anchor_bar_vertical_spacing', { unit_measure: '' }))
            .required(),
        inclination: Yup.number()
            .label(t('anchor_bar_inclination', { unit_measure: '' }))
            .min(0)
            .max(360)
            .test({
                name: 'inclination',
                message: '' + t('yup_custom.rock_slope.inclination'),
                test: val => {
                    return macro1State.project.data?.rockSlope?.slopeAnchorAngleLimitUnlocked || (val ?? 0) + (Number(macro1State.project.data?.rockSlope?.inclination) ?? 0) < 90;
                },
            })
            .required(),
        coefficient: Yup.string()
            .label(t('anchor_bar_coefficient', { unit_measure: '' }))
            .required(),
        anchorType: Yup.number()
            .label(t('anchor_bar_anchor_type', { unit_measure: '' }))
            .required(),
        anchorTypeName: Yup.string().required(),
        innerDiameter: Yup.number()
            .label(t('anchor_bar_inner_diameter', { unit_measure: '' }))
            .min(0)
            .required(),
        externalDiameter: Yup.number()
            .label(t('anchor_bar_external_diameter', { unit_measure: '' }))
            .min(0)
            .moreThan(Yup.ref('innerDiameter'))
            .required(),
        drillingDiameter: Yup.number()
            .label(t('anchor_bar_drilling_diameter', { unit_measure: '' }))
            .min(0)
            .moreThan(Yup.ref('externalDiameter'))
            .required(),
        yieldTensileStress: Yup.number()
            .label(t('anchor_bar_yield_tensile_stress', { unit_measure: '' }))
            .required(),
        customThickness: Yup.number().when('automaticThickness', ([automaticThickness], sch) => {
            if (!automaticThickness)
                return Yup.number()
                    .required()
                    .min(0)
                    .label(t('anchor_bar_custom_thickness', { unit_measure: '' }));
            return Yup.number()
                .notRequired()
                .label(t('anchor_bar_custom_thickness', { unit_measure: '' }));
        }),
        automaticThickness: Yup.boolean().label(t('anchor_bar_automatic_thickness')),
        environmentalAgressivity: Yup.string().when('automaticThickness', ([automaticThickness], sch) => {
            if (automaticThickness) return sch.required().label(t('anchor_bar_environmental_aggressivity'));
            return sch.notRequired().label(t('anchor_bar_environmental_aggressivity'));
        }),
        lifespan: Yup.number().when('automaticThickness', ([automaticThickness], sch) => {
            if (automaticThickness) return sch.required().min(0).label(t('anchor_bar_lifespan'));
            return sch.notRequired().label(t('anchor_bar_lifespan'));
        }),
        NFP94270: Yup.boolean().label(t('anchor_bar_NFP94270')),
        bondStress: Yup.number().min(0).label(t('anchor_bar_bond_stress')).required(),
        assumedPlasticization: Yup.number().label(t('anchor_bar_assumed_plasticization')).min(0).required(),
    });

    const dispatchAnchorBar = (payload: IAnchorBarForm) => {
        dispatch(setAnchorBar(payload));
    };

    const getMeasurementUnit = (unit: string[]) => {
        return ' [' + unit[projectInfo.data?.unit == 'Metric' ? 0 : 1] + ']';
    };

    const formikProps = useFormik({
        initialValues: macro1State.project?.data?.anchorBar ?? AnchorBarDefault,
        validationSchema: AnchorBarValidationSchema,
        onSubmit: (values, { setSubmitting }) => {
            setSubmitting(false);
        },
    });

    useEffect(() => {
        if (!!projectInfo.data?.unit) {
            getMacro1AnchorTypes().then(response => {
                setAnchorTypesList(response.data);
                if (!formikProps.values.customAnchorType) {
                    const selectedAnchor = response.data.find(mt => mt.id == formikProps.values.anchorType) ?? response.data.find(mt => mt.typeName == 'Steel bars B450C') ?? response.data[0];
                    if (selectedAnchor) {
                        setExternalDiametersList(selectedAnchor.externalDiameter.map(ed => ed.toFixed(2)));
                        if (formikProps.values.anchorType == 0) {
                            formikProps.setFieldValue('anchorType', selectedAnchor.id);
                            formikProps.setFieldValue('anchorTypeName', selectedAnchor.typeName);
                            if (!selectedAnchor.externalDiameter.includes(Number(formikProps.values.externalDiameter))) {
                                formikProps.setFieldValue('externalDiameter', selectedAnchor.externalDiameter[0]);
                            }
                            formikProps.setFieldValue('innerDiameter', selectedAnchor.internalDiameter);
                            formikProps.setFieldValue('yieldTensileStress', selectedAnchor.yieldStressOfSteel);
                        }
                    }
                }
            });
        }
        console.log('formikProps.values.anchorType', formikProps.values.anchorType);
    }, [projectInfo.data?.unit, window.localStorage.getItem('i18nextLng')]);

    useEffect(() => {
        if (!formikProps.values.customAnchorType && anchorTypesList) {
            const selectedAnchor = anchorTypesList.find(mt => mt.id == formikProps.values.anchorType) ?? anchorTypesList.find(mt => mt.typeName == 'Steel bars B450C') ?? anchorTypesList[0];
            if (selectedAnchor) {
                setExternalDiametersList(selectedAnchor.externalDiameter.map(ed => ed.toFixed(2)));
                formikProps.setFieldValue('anchorTypeName', selectedAnchor.typeName);
                if (!selectedAnchor.externalDiameter.includes(Number(formikProps.values.externalDiameter))) {
                    formikProps.setFieldValue('externalDiameter', selectedAnchor.externalDiameter[0].toFixed(2));
                }
                formikProps.setFieldValue('innerDiameter', selectedAnchor.internalDiameter);
                formikProps.setFieldValue('yieldTensileStress', projectInfo.data?.unit == 'Metric' ? selectedAnchor.yieldStressOfSteel : selectedAnchor.yieldStressOfSteel.toFixed(0));
            }
        }
    }, [formikProps.values.anchorType, formikProps.values.customAnchorType, window.localStorage.getItem('i18nextLng')]);

    const handleValueChanged = (valueKey: string, value: any) => {
        formikProps.setFieldValue(valueKey, value);
    };

    useEffect(() => {
        dispatchAnchorBar(formikProps.values);
    }, [formikProps.values]);

    useEffect(() => {
        formikProps.validateField('inclination');
    }, [macro1State.project.data?.rockSlope?.inclination]);

    const currentResults = useAppSelector((state: RootState) => {
        return { ...state.Macro1.results.data };
    });
    useEffect(() => {
        if (currentResults.validation?.startValidation && !currentResults.validation?.anchorBar) {
            formikProps.submitForm();
            formikProps.validateForm().then(errors => {
                if (currentResults.validation)
                    dispatch(
                        updateValidation({
                            ...currentResults.validation,
                            anchorBar: true,
                            anchorBarErrors: Object.keys(errors).length > 0,
                        }),
                    );
            });
        }
    }, [JSON.stringify(currentResults.validation)]);

    return (
        <div className='style-form'>
            <FormikProvider value={formikProps}>
                <Grid container spacing={3}>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('anchor_bar_section_geometry')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <div className='d-flex'>
                            <label>{t('anchor_bar_horizontal_spacing', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                            <i
                                onClick={() => {
                                    openDialog('anchor_spacing');
                                }}
                                className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon'
                            />
                        </div>
                        <FieldNumber name='horizontalSpacing' step='0.01' disabled={props.readOnly} />
                        <ErrorMessage name='horizontalSpacing' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_horizontal_spacing')}</label>
                    </div>
                    <Grid item xs={11}>
                        <div className='d-flex'>
                            <label>{t('anchor_bar_vertical_spacing', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                            <i
                                onClick={() => {
                                    openDialog('anchor_spacing');
                                }}
                                className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon'
                            />
                        </div>
                        <FieldNumber name='verticalSpacing' step='0.01' disabled={props.readOnly} />
                        <ErrorMessage name='verticalSpacing' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_vertical_spacing')}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('anchor_bar_inclination', { unit_measure: getMeasurementUnit(conversionLabel.deg) })}</label>
                        <FieldNumber name='inclination' step='0.01' disabled={props.readOnly} />
                        <ErrorMessage name='inclination' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_inclination')}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('anchor_bar_coefficient', { unit_measure: '' })}</label>
                        <Field
                            as={MuiSelect}
                            name='coefficient'
                            value={formikProps.values.coefficient}
                            onChange={(e: any) => handleValueChanged('coefficient', e.target.value)}
                            disabled={props.readOnly}>
                            <MenuItem value={1}>{t('anchor_bar_coefficient_options.no_increment')}</MenuItem>
                            <MenuItem value={1.1}>{t('anchor_bar_coefficient_options.drilling_in_rock')}</MenuItem>
                            <MenuItem value={1.2}>{t('anchor_bar_coefficient_options.drilling_in_soil')}</MenuItem>
                        </Field>
                        <ErrorMessage name='coefficient' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_coefficient')}</label>
                    </div>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('anchor_bar_section_anchor_type')}</label>
                    </Grid>
                    <Grid item xs={12}>
                        <label>{t('anchor_bar_anchor_type_custom', { unit_measure: '' })}</label>
                        <Field type='checkbox' name='customAnchorType' disabled={props.readOnly} />
                        <ErrorMessage name='customAnchorType' component='div' className='text-danger' />
                    </Grid>
                    {formikProps.values.customAnchorType ? (
                        <Grid item xs={12}>
                            <label>{t('anchor_bar_anchor_type', { unit_measure: '' })}</label>
                            <Field type='text' name='anchorTypeName' disabled={props.readOnly} />
                            <ErrorMessage name='anchorTypeName' component='div' className='text-danger' />
                        </Grid>
                    ) : (
                        <Grid item xs={12}>
                            <label>{t('anchor_bar_anchor_type', { unit_measure: '' })}</label>
                            <Field
                                as={MuiSelect}
                                name='anchorType'
                                value={formikProps.values.anchorType}
                                onChange={(e: any) => handleValueChanged('anchorType', e.target.value)}
                                disabled={props.readOnly}>
                                {anchorTypesList.map(at => {
                                    return (
                                        <MenuItem key={at.id} value={at.id}>
                                            {at.typeName}
                                        </MenuItem>
                                    );
                                })}
                            </Field>
                            <ErrorMessage name='anchorType' component='div' className='text-danger' />
                        </Grid>
                    )}
                    <Grid item xs={11}>
                        <label>{t('anchor_bar_inner_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        <FieldNumber name='innerDiameter' step='0.01' disabled={props.readOnly ? true : !formikProps.values.customAnchorType} />
                        <ErrorMessage name='innerDiameter' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_inner_diameter')}</label>
                    </div>
                    {formikProps.values.customAnchorType ? (
                        <Grid item xs={11}>
                            <label>{t('anchor_bar_external_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                            <FieldNumber name='externalDiameter' step='0.01' disabled={props.readOnly} />
                            <ErrorMessage name='externalDiameter' component='div' className='text-danger' />
                        </Grid>
                    ) : (
                        <Grid item xs={11}>
                            <label>{t('anchor_bar_external_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                            <Field
                                as={MuiSelect}
                                name='externalDiameter'
                                value={formikProps.values.externalDiameter}
                                onChange={(e: any) => handleValueChanged('externalDiameter', e.target.value)}
                                disabled={props.readOnly}>
                                {externalDiametersList.map(ed => {
                                    return (
                                        <MenuItem key={ed} value={ed}>
                                            {ed}
                                        </MenuItem>
                                    );
                                })}
                            </Field>
                            <ErrorMessage name='externalDiameter' component='div' className='text-danger' />
                        </Grid>
                    )}
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_external_diameter')}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('anchor_bar_drilling_diameter', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        <FieldNumber name='drillingDiameter' step='0.01' disabled={props.readOnly} />
                        <ErrorMessage name='drillingDiameter' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_drilling_diameter')}</label>
                    </div>
                    <Grid item xs={11}>
                        <label>{t('anchor_bar_yield_tensile_stress', { unit_measure: getMeasurementUnit(conversionLabel.mpa_ksi) })}</label>
                        <FieldNumber name='yieldTensileStress' disabled={props.readOnly ? true : !formikProps.values.customAnchorType} />
                        <ErrorMessage name='yieldTensileStress' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_yield_tensile_stress')}</label>
                    </div>
                    <Grid item xs={12}>
                        <Divider />
                    </Grid>
                    <Grid item xs={12}>
                        <label className='form-section-title'>{t('anchor_bar_section_corrosion_crown')}</label>
                    </Grid>
                    <Grid item xs={11}>
                        <label>{t('anchor_bar_custom_thickness', { unit_measure: getMeasurementUnit(conversionLabel.mm_in) })}</label>
                        <FieldNumber disabled={props.readOnly ? true : formikProps.values.automaticThickness} name='customThickness' step='0.01' />
                        <ErrorMessage name='customThickness' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_custom_thickness')}</label>
                    </div>
                    {/*<Grid item xs={12}>
            <label>{t('anchor_bar_automatic_thickness', { unit_measure: '' })}</label>
            <Field type="checkbox" name="automaticThickness" />
            <ErrorMessage name="automaticThickness" component="div" className="text-danger" />
        </Grid>*/}
                    {formikProps.values.automaticThickness ? (
                        <>
                            <Grid item xs={11} style={{ paddingLeft: 48 }}>
                                <label>{t('anchor_bar_environmental_aggressivity', { unit_measure: getMeasurementUnit(conversionLabel.mmy_iny) })}</label>
                                <Field
                                    as={MuiSelect}
                                    name='environmentalAgressivity'
                                    value={formikProps.values.environmentalAgressivity}
                                    onChange={(e: any) => handleValueChanged('environmentalAgressivity', e.target.value)}
                                    disabled={props.readOnly}>
                                    <MenuItem value={'Low'}>{t('anchor_bar_environmental_aggressivity_options.low')}</MenuItem>
                                    <MenuItem value={'Medium'}>{t('anchor_bar_environmental_aggressivity_options.medium')}</MenuItem>
                                    <MenuItem value={'High'}>{t('anchor_bar_environmental_aggressivity_options.high')}</MenuItem>
                                </Field>
                                <ErrorMessage name='environmentalAgressivity' component='div' className='text-danger' />
                            </Grid>
                            <div className='symbol-wrap'>
                                <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_environmental_aggressivity')}</label>
                            </div>
                            <Grid item xs={11} style={{ paddingLeft: 48 }}>
                                <label>{t('anchor_bar_lifespan', { unit_measure: '' })}</label>
                                <FieldNumber name='lifespan' step='0.01' disabled={props.readOnly} />
                                <ErrorMessage name='lifespan' component='div' className='text-danger' />
                            </Grid>
                            <div className='symbol-wrap'>
                                <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_lifespan')}</label>
                            </div>
                            {/* <Grid item xs={12} style={{ paddingLeft: 48 }}>
                <label>{t('anchor_bar_NFP94270', { unit_measure: '' })}</label>
                <Field type="checkbox" name="NFP94270" />
                <ErrorMessage name="NFP94270" component="div" className="text-danger" />
              </Grid> */}
                            <Grid style={{ marginLeft: 24 }} item xs={12}>
                                <Divider />
                            </Grid>
                        </>
                    ) : (
                        <></>
                    )}
                    <Grid item xs={11}>
                        <div className='d-flex'>
                            <label>{t('anchor_bar_bond_stress', { unit_measure: getMeasurementUnit(conversionLabel.mpa_psi) })}</label>
                            <i
                                onClick={() => {
                                    openDialog('rock_grout');
                                }}
                                className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon'
                            />
                        </div>
                        <FieldNumber name='bondStress' step='0.01' disabled={props.readOnly} />
                        <ErrorMessage name='bondStress' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_bond_stress')}</label>
                    </div>
                    <Grid item xs={11}>
                        <div className='d-flex'>
                            <label>{t('anchor_bar_assumed_plasticization', { unit_measure: getMeasurementUnit(conversionLabel.m_ft) })}</label>
                            <i
                                onClick={() => {
                                    openDialog('assumed_plasticization');
                                }}
                                className='fa-sharp fa-solid fa-circle-info fs-5 align-self-end info-icon'
                            />
                        </div>
                        <FieldNumber name='assumedPlasticization' step='0.01' disabled={props.readOnly} />
                        <ErrorMessage name='assumedPlasticization' component='div' className='text-danger' />
                    </Grid>
                    <div className='symbol-wrap'>
                        <label className='symbol'>{t('anchor_bar_symbols.anchor_bar_assumed_plasticization')}</label>
                    </div>
                </Grid>
            </FormikProvider>
            <Dialog open={CurrentDialog == 'anchor_spacing'} title={`${t('macro1_info_dialogs_anchor_spacing')}`} onClose={closeDialog}>
                <div>
                    <img width='100%' height='100%' srcSet={`${imgAnchorSpacing}?fit=crop&auto=format&dpr=2 2x`} src={`${imgAnchorSpacing}?fit=crop&auto=format&dpr=2 2x`} alt='Anchor Spacing'></img>
                </div>
            </Dialog>
            <Dialog open={CurrentDialog == 'rock_grout'} title={`${t('macro1_info_dialogs_rock_grout')}`} onClose={closeDialog}>
                <div>
                    <img width='100%' height='100%' srcSet={`${rockGroutSrc}?fit=crop&auto=format&dpr=2 2x`} src={`${rockGroutSrc}?fit=crop&auto=format&dpr=2 2x`} alt='Rock Grout'></img>
                </div>
            </Dialog>
            <Dialog open={CurrentDialog == 'assumed_plasticization'} title={`${t('macro1_info_dialogs_assumed_plasticization')}`} onClose={closeDialog}>
                <div>
                    <img
                        width='100%'
                        height='100%'
                        srcSet={`${imgAssumedPlasticization}?fit=crop&auto=format&dpr=2 2x`}
                        src={`${imgAssumedPlasticization}?fit=crop&auto=format&dpr=2 2x`}
                        alt='Assumed Plasticization'></img>
                </div>
            </Dialog>
        </div>
    );
};

export default AnchorBar;
